/**
 * ExportSelectorDialog - Hierarchical workspace/board selector for selective export.
 * Shows a tree of workspaces with their boards, allowing users to check/uncheck
 * specific workspaces and boards before exporting.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Download, Kanban, Layout, Loader2 } from 'lucide-react';
import { BidiText } from '../../../shared/components/common';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { ResponsiveDialog } from '../../../shared/components/common/ResponsiveDialog';
import { useExportTree, type ExportTreeWorkspace } from '../hooks/useExportImport';

export type TaskExportFormat = 'structured' | 'flat';

interface ExportSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (workspaceIds: string[], boardIds: string[], format: TaskExportFormat) => void;
  isExporting: boolean;
}

export function ExportSelectorDialog({
  open,
  onOpenChange,
  onExport,
  isExporting,
}: ExportSelectorDialogProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const { data: tree, isLoading } = useExportTree(open);

  const [format, setFormat] = useState<TaskExportFormat>('structured');
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Set<string>>(new Set());
  const [selectedBoards, setSelectedBoards] = useState<Set<string>>(new Set());
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set());

  // Select all by default when tree loads
  useEffect(() => {
    if (tree && tree.length > 0) {
      setSelectedWorkspaces(new Set(tree.map((ws) => ws.id)));
      setSelectedBoards(new Set(tree.flatMap((ws) => ws.boards.map((b) => b.id))));
      setExpandedWorkspaces(new Set(tree.map((ws) => ws.id)));
    }
  }, [tree]);

  const allBoardIdsByWorkspace = useMemo(() => {
    if (!tree) return new Map<string, string[]>();
    return new Map(tree.map((ws) => [ws.id, ws.boards.map((b) => b.id)]));
  }, [tree]);

  const toggleWorkspace = useCallback(
    (wsId: string) => {
      const newWs = new Set(selectedWorkspaces);
      const newBoards = new Set(selectedBoards);
      const wsBoardIds = allBoardIdsByWorkspace.get(wsId) ?? [];

      if (newWs.has(wsId)) {
        newWs.delete(wsId);
        for (const bid of wsBoardIds) newBoards.delete(bid);
      } else {
        newWs.add(wsId);
        for (const bid of wsBoardIds) newBoards.add(bid);
      }

      setSelectedWorkspaces(newWs);
      setSelectedBoards(newBoards);
    },
    [selectedWorkspaces, selectedBoards, allBoardIdsByWorkspace],
  );

  const toggleBoard = useCallback(
    (wsId: string, boardId: string) => {
      const newBoards = new Set(selectedBoards);
      const newWs = new Set(selectedWorkspaces);

      if (newBoards.has(boardId)) {
        newBoards.delete(boardId);
        // If no boards left in this workspace, deselect workspace too
        const wsBoardIds = allBoardIdsByWorkspace.get(wsId) ?? [];
        if (!wsBoardIds.some((bid) => newBoards.has(bid))) {
          newWs.delete(wsId);
        }
      } else {
        newBoards.add(boardId);
        // Auto-select workspace when a board is selected
        newWs.add(wsId);
      }

      setSelectedWorkspaces(newWs);
      setSelectedBoards(newBoards);
    },
    [selectedBoards, selectedWorkspaces, allBoardIdsByWorkspace],
  );

  const toggleExpanded = useCallback((wsId: string) => {
    setExpandedWorkspaces((prev) => {
      const next = new Set(prev);
      if (next.has(wsId)) next.delete(wsId);
      else next.add(wsId);
      return next;
    });
  }, []);

  const isAllSelected = tree
    ? selectedWorkspaces.size === tree.length
    : false;

  const toggleAll = useCallback(() => {
    if (!tree) return;
    if (isAllSelected) {
      setSelectedWorkspaces(new Set());
      setSelectedBoards(new Set());
    } else {
      setSelectedWorkspaces(new Set(tree.map((ws) => ws.id)));
      setSelectedBoards(new Set(tree.flatMap((ws) => ws.boards.map((b) => b.id))));
    }
  }, [tree, isAllSelected]);

  const hasSelection = selectedWorkspaces.size > 0 || selectedBoards.size > 0;

  const isWorkspacePartial = useCallback(
    (ws: ExportTreeWorkspace) => {
      if (!selectedWorkspaces.has(ws.id)) return false;
      const wsBoardIds = ws.boards.map((b) => b.id);
      const selectedCount = wsBoardIds.filter((bid) => selectedBoards.has(bid)).length;
      return selectedCount > 0 && selectedCount < wsBoardIds.length;
    },
    [selectedWorkspaces, selectedBoards],
  );

  const handleExport = useCallback(() => {
    // Determine if this is selective or all
    const allWsSelected = tree && selectedWorkspaces.size === tree.length;
    const allBoardsSelected = tree && selectedBoards.size === tree.flatMap((ws) => ws.boards).length;

    if (allWsSelected && allBoardsSelected) {
      // Export all - no filters
      onExport([], [], format);
    } else {
      onExport(Array.from(selectedWorkspaces), Array.from(selectedBoards), format);
    }
  }, [tree, selectedWorkspaces, selectedBoards, format, onExport]);

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('tasks:exportImport.selectiveExportTitle')}
      className="max-w-md"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('tasks:exportImport.selectiveExportDescription')}
        </p>

        {/* Format choice: round-trip package vs flat rows for import/BI */}
        <div className="space-y-2">
          <span className="text-sm font-medium">
            {t('tasks:exportImport.formatTitle')}
          </span>
          <RadioGroup
            value={format}
            onValueChange={(value) => setFormat(value as TaskExportFormat)}
            className="gap-2"
          >
            <div className="flex items-start gap-2">
              <RadioGroupItem value="structured" id="export-format-structured" className="mt-0.5" />
              <Label htmlFor="export-format-structured" className="cursor-pointer font-normal">
                <span className="text-sm">{t('tasks:exportImport.formatStructured')}</span>
                <span className="block text-xs text-muted-foreground">
                  {t('tasks:exportImport.formatStructuredHint')}
                </span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="flat" id="export-format-flat" className="mt-0.5" />
              <Label htmlFor="export-format-flat" className="cursor-pointer font-normal">
                <span className="text-sm">{t('tasks:exportImport.formatFlat')}</span>
                <span className="block text-xs text-muted-foreground">
                  {t('tasks:exportImport.formatFlatHint')}
                </span>
              </Label>
            </div>
          </RadioGroup>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : tree && tree.length > 0 ? (
          <>
            {/* Select All / Deselect All */}
            <div className="flex items-center justify-between border-b pb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAll}
                className="text-xs"
              >
                {isAllSelected
                  ? t('tasks:exportImport.deselectAll')
                  : t('tasks:exportImport.selectAll')}
              </Button>
            </div>

            {/* Workspace/Board Tree */}
            <div className="max-h-[50vh] space-y-1 overflow-y-auto">
              {tree.map((ws) => (
                <div key={ws.id}>
                  {/* Workspace row */}
                  <div className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(ws.id)}
                      className="shrink-0"
                    >
                      {ws.boards.length > 0 ? (
                        expandedWorkspaces.has(ws.id) ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )
                      ) : (
                        <div className="h-4 w-4" />
                      )}
                    </button>
                    <Checkbox
                      checked={
                        isWorkspacePartial(ws)
                          ? 'indeterminate'
                          : selectedWorkspaces.has(ws.id)
                      }
                      onCheckedChange={() => toggleWorkspace(ws.id)}
                    />
                    <Layout className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <BidiText className="truncate text-sm font-medium">{ws.name}</BidiText>
                    {ws.boards.length > 0 && (
                      <span className="ms-auto text-xs text-muted-foreground">
                        {ws.boards.length}
                      </span>
                    )}
                  </div>

                  {/* Board rows */}
                  {expandedWorkspaces.has(ws.id) &&
                    ws.boards.map((board) => (
                      <div
                        key={board.id}
                        className="flex items-center gap-2 rounded-md py-1.5 pe-2 ps-10 hover:bg-accent"
                      >
                        <Checkbox
                          checked={selectedBoards.has(board.id)}
                          onCheckedChange={() => toggleBoard(ws.id, board.id)}
                        />
                        <Kanban className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <BidiText className="truncate text-sm">{board.name}</BidiText>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('tasks:dashboard.noWorkspaces')}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            {t('common:app.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={!hasSelection || isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 me-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 me-2" />
            )}
            {isAllSelected
              ? t('tasks:exportImport.exportAll')
              : t('tasks:exportImport.exportSelected')}
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
